import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * Base do design system. Nenhuma tela escreve cor ou espaçamento literal:
 * tudo vem de token, para a aplicação parecer um produto único e não um conjunto
 * de telas montadas em separado.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-brand-hover',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // O par do `destructive` para a ação afirmativa — aprovar, confirmar, liberar. Verde é
        // sucesso, como no aviso e na etiqueta de situação; a decisão se lê pela cor antes do texto.
        success: 'bg-success text-success-foreground hover:bg-success/90',
        // Ação que pede atenção mas tem volta (pausar): o laranja de alerta do tema.
        warning: 'bg-warning text-warning-foreground hover:bg-warning/90',
        link: 'text-link',
      },
      size: {
        sm: 'h-8 rounded-md px-3',
        default: 'h-9 px-4 py-2',
        lg: 'h-10 rounded-md px-6',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Component = asChild ? Slot : 'button';

  return (
    <Component
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
